import { logger } from '@/lib/logger';

type Saved = {
  warn: typeof console.warn;
  error: typeof console.error;
  info: typeof console.info;
  debug: typeof console.debug;
};

const KEY = Symbol.for('__logger_console_patch__');

const getPatchedGlobal = (): typeof globalThis & { [KEY]?: Saved } => {
  return globalThis as typeof globalThis & { [KEY]?: Saved };
};

const serializeArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg;
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (arg instanceof Error) return arg.stack ?? `${arg.name}: ${arg.message}`;
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
};

const serializeArgs = (args: unknown[]): string =>
  args.map(serializeArg).join(' ');

const HLS_TOKEN_ERROR_PATTERNS = [
  /manifestIncompatibleCodecsError/,
  /getErrorFromHlsErrorData/,
];

type HlsErrorData = {
  details?: string;
  response?: { code?: number };
  url?: string;
};

const isHlsStaleTokenError = (message: string, args: unknown[]): boolean => {
  const hasKnownPattern = HLS_TOKEN_ERROR_PATTERNS.some(pattern =>
    pattern.test(message),
  );
  if (!hasKnownPattern) {
    return false;
  }

  const data = args.find(
    (a): a is HlsErrorData =>
      typeof a === 'object' && a !== null && 'details' in a,
  );
  if (!data) {
    return true;
  }

  if (data.details === 'manifestLoadError') {
    return (
      data.response?.code === 403 ||
      (typeof data.url === 'string' && data.url.includes('stream.mux.com'))
    );
  }

  return true;
};

export const interceptConsole = (): void => {
  const patchedGlobal = getPatchedGlobal();
  /* 1- Guard: already patched in this runtime? */
  if (patchedGlobal[KEY]) return;

  /* 2- Save originals & expose for ConsolePlugin to use as its output channel */
  patchedGlobal[KEY] = {
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug ?? console.log,
  };

  /* 3- Patch: forward to logger only — ConsolePlugin handles the actual output */
  console.warn = (...a) => {
    logger.warn(serializeArgs(a));
  };
  console.debug = (...a) => {
    logger.debug(serializeArgs(a));
  };
  console.info = (...a) => {
    logger.info(serializeArgs(a));
  };
  console.error = (...a) => {
    const message = serializeArgs(a);

    if (isHlsStaleTokenError(message, a)) {
      return;
    }

    const err = a[0] instanceof Error ? (a[0] as Error) : new Error(message);
    logger.error(err, { source: 'console.error' });
  };
};
